import { NG_VALUE_ACCESSOR, NG_VALIDATORS } from '@angular/forms';
import { FiFormService } from '@fi-app/fi-form/fi-form.service';
import {
    Component,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    Input,
    ViewChild,
    ElementRef,
    ChangeDetectorRef,
    Injector,
} from '@angular/core';
import { IFormCondition, IFormEdit } from '@fi-shared/fi-form-shared/fi-form-shared';
import { ControlValueAccessorValidatorBase } from '@fi-app/shared/control-value-accessor-validator-base';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiFormLogicConditionEditorDialogComponent } from '@fi-app/fi-form/fi-form-edit/fi-form-edit-logic/fi-form-logic-condition-editor-dialog/fi-form-logic-condition-editor-dialog.component';
import { IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { FiFormConditionHelperService } from '@fi-shared/fi-form-shared/fi-form-condition-helper.service';
import { removeItem } from '@fi-shared/utils/remove-item';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';

@Component({
    selector: 'fi-form-edit-logic',
    templateUrl: './fi-form-edit-logic.component.html',
    styleUrls: ['./fi-form-edit-logic.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiFormEditLogicComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiFormEditLogicComponent,
        },
    ],
    standalone: false,
})
export class FiFormEditLogicComponent extends ControlValueAccessorValidatorBase<IFormEdit> {
    @Input() selectedLanguageId: number;
    @ViewChild('formLogicContainer') formLogicContainer: ElementRef;
    get form(): IFormEdit {
        return this.ngModel;
    }

    constructor(
        private injector: Injector,
        private formHelperService: FiFormService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private formConditionHelperService: FiFormConditionHelperService,
        private saveChangesWarningService: SaveChangesWarningService,
    ) {
        super(injector);
    }

    deleteCondition($event: IFormCondition) {
        removeItem(this.form.Conditions, $event);
    }

    getConditionNumber(conditionNumber: number): string {
        return conditionNumber > 9 ? `${conditionNumber}` : `0${conditionNumber}`;
    }

    addNewCondition() {
        const dialogHeader = this.appTextService.get('form.new_condition');
        const condition = this.formHelperService.getNewConditionInstance();

        this.openConditionEditorDialog(condition, dialogHeader)
            .onYesButtonClick((result) => {
                this.form.Conditions.push(result);
                this.cdr.detectChanges();
            })
            .afterClosed(() => (this.saveChangesWarningService.closeDialogOnTransition = false));
        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    editCondition(condition: IFormCondition, conditionNumber: number) {
        const dialogHeader =
            this.appTextService.get('form.condition_editing') + ' ' + this.getConditionNumber(conditionNumber);

        this.openConditionEditorDialog(condition, dialogHeader)
            .onYesButtonClick((result) => {
                this.form.Conditions.splice(this.form.Conditions.indexOf(condition), 1, result);
                this.cdr.detectChanges();
            })
            .afterClosed(() => (this.saveChangesWarningService.closeDialogOnTransition = false));
        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    private openConditionEditorDialog(
        condition: IFormCondition,
        dialogHeader: string,
    ): IFiDialogResult<IFormCondition> {
        return this.fiDialogsService.custom({
            contentComponentRef: FiFormLogicConditionEditorDialogComponent,
            contentComponentData: {
                condition,
                formQuestions: this.form.Questions,
                selectedLanguageId: this.selectedLanguageId,
            },
            headerText: dialogHeader,
            config: {
                minWidth: '650px',
                panelClass: 'fi-form-logic-condition-editor-dialog-panel',
                closeOnNavigation: false,
            },
        });
    }

    doValidate() {
        return {
            required: this.form.Conditions.some((condition) => {
                return !this.formConditionHelperService.isConditionComplete(condition, this.form.Questions);
            }),
        };
    }
}
